import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';
import { getAnnouncementUrl } from '../../utils/slugify';
import { getApiEndpoint, getUploadUrl } from '../../config/api';

const AdminPanel = () => {
  const [user, setUser] = useState(null);
  const [activeCategory, setActiveCategory] = useState('publish'); // 'publish' or 'announcements'
  const [announcements, setAnnouncements] = useState([]);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  
  // Add meta tags to prevent indexing
  useEffect(() => {
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
    }
  }, []);
  
  const [anuntData, setAnuntData] = useState({
    title: '',
    category: '',
    subcategory: '',
    subsubcategory: '',
    manufacturer: '',
    model: '',
    year: '',
    price: '',
    negotiable: true,
    featured: 0,
    condition: 'second-hand',
    hours: '',
    hoursUnit: 'hours', // 'hours' or 'km'
    weight: '',
    power: '',
    capacity: '',
    features: '',
    description: '',
    location: '',
    contact: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]); // For editing mode
  const [imagesToDelete, setImagesToDelete] = useState([]); // Track images to delete
  
  // Video state
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]); // For editing mode
  const [videosToDelete, setVideosToDelete] = useState([]); // Track videos to delete
  
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Complete category structure from Filters.jsx
  const categoryStructure = [
    {
      id: 'utilaje-constructii',
      name: 'Utilaje de Construcții',
      subcategories: [
        { 
          id: 'excavatoare', 
          name: 'Excavatoare', 
          items: ['excavatoare pe șenile', 'excavatoare pe roți', 'excavatoare pentru fier vechi', 'mini excavatoare'] 
        },
        { id: 'buldoexcavatoare', name: 'Buldoexcavatoare' },
        { id: 'buldozere', name: 'Buldozere' },
        { 
          id: 'cilindri-compactori', 
          name: 'Cilindri compactori',
          items: ['Cilindri compactori', 'Cilindri mici', 'Cilindri tandem', 'Cilindri Polipneu']
        },
        { id: 'gredere', name: 'Gredere' },
        { 
          id: 'incarcatoare', 
          name: 'Încărcătoare',
          items: ['Încărcătoare frontale', 'Încărcătoare telescopice', 'Mini încărcătoare']
        },
        { id: 'dumpere', name: 'Dumpere (Camioane de șantier)' },
        { id: 'automacarale', name: 'Automacarale' },
        { id: 'autobetoniere', name: 'Autobetoniere' },
        { id: 'masini-asfalt', name: 'Mașini de asfalt' },
        { id: 'sivuitoare', name: 'Sivuitoare' },
        { id: 'nacele', name: 'Nacele' },
        { id: 'statii-sortare', name: 'Stații de sortare' },
        { id: 'concasoare', name: 'Concasoare' },
        { id: 'accesorii-echipamente', name: 'Accesorii și echipamente pentru utilaje' },
        { id: 'dezmembrari', name: 'Dezmembrări utilaje' },
        { id: 'diverse', name: 'Diverse' }
      ]
    },
    {
      id: 'transport',
      name: 'Camioane (Transport)',
      subcategories: [
        { id: 'camioane', name: 'Camioane' },
        { id: 'trailere', name: 'Trailere' },
        { id: 'semiremorci', name: 'Semiremorci' },
        { id: 'matura-stradala', name: 'Mătură stradală' }
      ]
    },
    {
      id: 'utilaje-agricole',
      name: 'Utilaje Agricole',
      subcategories: [
        { id: 'tractoare', name: 'Tractoare' },
        { id: 'combine', name: 'Combine' },
        { id: 'echipamente-agricole', name: 'Echipamente agricole' }
      ]
    },
    {
      id: 'autoturisme-autoutilitare',
      name: 'Autoturisme și autoutilitare',
      subcategories: [
        { id: 'autoturisme', name: 'Autoturisme' },
        { id: 'autoutilitare', name: 'Autoutilitare (camionete)' },
        { id: 'remorci', name: 'Remorci' }
      ]
    }
  ];

  // Get subcategories based on selected main category
  const getSubcategories = () => {
    const mainCat = categoryStructure.find(cat => cat.id === anuntData.category);
    return mainCat ? mainCat.subcategories : [];
  };

  // Get sub-subcategories based on selected subcategory
  const getSubSubcategories = () => {
    const subcategories = getSubcategories();
    const subCat = subcategories.find(sub => sub.id === anuntData.subcategory);
    return subCat && subCat.items ? subCat.items : [];
  };

  // Handle category change - reset subcategory and subsubcategory
  const handleCategoryChange = (e) => {
    setAnuntData({
      ...anuntData,
      category: e.target.value,
      subcategory: '',
      subsubcategory: ''
    });
  };

  // Handle subcategory change - reset subsubcategory
  const handleSubcategoryChange = (e) => {
    setAnuntData({
      ...anuntData,
      subcategory: e.target.value,
      subsubcategory: ''
    });
  };

  // Check if user is logged in
  useEffect(() => {
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      navigate('/admin-login');
    }
  }, [navigate]);

  // Fetch all announcements when switching to announcements category
  useEffect(() => {
    if (activeCategory === 'announcements') {
      fetchAllAnnouncements();
    }
  }, [activeCategory]);

  // Fetch all announcements
  const fetchAllAnnouncements = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/announcements/all'));
      const data = await response.json();
      
      if (data.success) {
        setAnnouncements(data.announcements);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setError('Failed to load announcements');
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`📝 Field changed: ${name} = "${value}"`);
    
    setAnuntData({
      ...anuntData,
      [name]: value
    });
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Calculate total images including existing ones (for edit mode)
    const totalImages = existingImages.length + selectedImages.length + files.length;
    
    // Limit to 10 images total
    if (totalImages > 10) {
      setError(`Maximum 10 images allowed. You have ${existingImages.length} existing + ${selectedImages.length} selected. Cannot add ${files.length} more.`);
      return;
    }
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setError('Only JPG, PNG, GIF, and WEBP images are allowed');
        return false;
      }
      
      if (file.size > maxSize) {
        setError('Each image must be less than 5MB');
        return false;
      }
      
      return true;
    });
    
    // Add new valid files to existing ones
    const updatedImages = [...selectedImages, ...validFiles];
    setSelectedImages(updatedImages);
    
    // Create preview URLs for new files
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setImagePreviews([...imagePreviews, ...newPreviews]);
    setError('');
    
    // Reset the file input to allow selecting the same file again if needed
    e.target.value = '';
  };

  // Remove image from selection
  const removeImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    
    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Handle video selection
  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Check total video count (max 3)
    const totalVideos = existingVideos.length + selectedVideos.length + files.length;
    if (totalVideos > 3) {
      setError(`Maximum 3 videos allowed. You have ${existingVideos.length} existing + ${selectedVideos.length} selected. Cannot add ${files.length} more.`);
      e.target.value = '';
      return;
    }

    // Validate file types
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/x-flv', 'video/webm', 'video/x-matroska'];
    const validFiles = [];
    
    for (const file of files) {
      if (allowedTypes.includes(file.type)) {
        // Check file size (max 300MB)
        if (file.size <= 300 * 1024 * 1024) {
          validFiles.push(file);
        } else {
          setError(`Video "${file.name}" is too large. Maximum size is 300MB.`);
        }
      } else {
        setError(`"${file.name}" is not a valid video format. Allowed: MP4, MOV, AVI, WMV, FLV, WebM, MKV`);
      }
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // Create video previews (just show filename for videos)
    const previews = validFiles.map(file => ({
      name: file.name,
      size: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
    }));
    
    const updatedVideos = [...selectedVideos, ...validFiles];
    setSelectedVideos(updatedVideos);
    setVideoPreviews([...videoPreviews, ...previews]);
    
    // Clear message after successful upload
    setTimeout(() => setError(''), 3000);
    e.target.value = '';
  };

  // Remove video from selection
  const removeVideo = (index) => {
    const newVideos = selectedVideos.filter((_, i) => i !== index);
    const newPreviews = videoPreviews.filter((_, i) => i !== index);
    
    setSelectedVideos(newVideos);
    setVideoPreviews(newPreviews);
  };

  // Handle publish announcement
  const handlePublishAnunt = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    console.log('📤 Publishing announcement with data:', anuntData);
    console.log('📋 Category fields:', {
      category: anuntData.category,
      subcategory: anuntData.subcategory,
      subsubcategory: anuntData.subsubcategory
    });

    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      // Append all text fields
      Object.keys(anuntData).forEach(key => {
        const value = anuntData[key];
        formData.append(key, value);
        if (key === 'category' || key === 'subcategory' || key === 'subsubcategory') {
          console.log(`✅ FormData appended: ${key} = "${value}"`);
        }
      });
      
      // Log all FormData entries
      console.log('📦 Complete FormData:');
      for (let pair of formData.entries()) {
        if (pair[0] === 'category' || pair[0] === 'subcategory' || pair[0] === 'subsubcategory') {
          console.log(`   ${pair[0]}: "${pair[1]}"`);
        }
      }
      
      // If editing, include existing images and images to delete
      if (editingAnnouncement) {
        formData.append('existingImages', existingImages.join(','));
        formData.append('imagesToDelete', imagesToDelete.join(','));
        formData.append('existingVideos', existingVideos.join(','));
        formData.append('videosToDelete', videosToDelete.join(','));
      }
      
      // Append new images
      selectedImages.forEach(image => {
        formData.append(editingAnnouncement ? 'newImages' : 'images', image);
      });

      // Append new videos
      selectedVideos.forEach(video => {
        formData.append(editingAnnouncement ? 'newVideos' : 'videos', video);
      });

      const url = editingAnnouncement 
        ? getApiEndpoint(`/api/announcements/${editingAnnouncement.id}`)
        : getApiEndpoint('/api/announcements/create');
      
      const method = editingAnnouncement ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        setError(errorData.message || `Server error: ${response.status}`);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setMessage(editingAnnouncement ? '✅ Anunț actualizat cu succes!' : '✅ Anunț publicat cu succes!');
        
        // Reset form after 2 seconds
        setTimeout(() => {
          resetForm();
          setMessage('');
          if (editingAnnouncement) {
            setActiveCategory('announcements');
            fetchAllAnnouncements();
          }
        }, 2000);
      } else {
        setError(data.message || 'Failed to publish announcement');
      }
    } catch (err) {
      console.error('Network error:', err);
      setError(`Network error: ${err.message || 'Could not connect to server'}`);
    }
  };

  // Start editing an announcement
  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnuntData({
      title: announcement.title,
      category: announcement.category,
      subcategory: announcement.subcategory || '',
      subsubcategory: announcement.subsubcategory || '',
      manufacturer: announcement.manufacturer,
      model: announcement.model,
      year: announcement.year,
      price: announcement.price,
      negotiable: announcement.negotiable,
      featured: announcement.featured || 0,
      condition: announcement.condition,
      hours: announcement.hours,
      hoursUnit: announcement.hoursUnit || 'hours',
      weight: announcement.weight || '',
      power: announcement.power || '',
      capacity: announcement.capacity || '',
      features: announcement.features || '',
      description: announcement.description,
      location: announcement.location,
      contact: announcement.contact || ''
    });
    
    // Set existing images
    if (announcement.images) {
      const imageArray = announcement.images.split(',').map(img => img.trim());
      setExistingImages(imageArray);
    } else {
      setExistingImages([]);
    }
    
    // Set existing videos
    if (announcement.videos) {
      const videoArray = announcement.videos.split(',').map(vid => vid.trim());
      setExistingVideos(videoArray);
    } else {
      setExistingVideos([]);
    }
    
    setSelectedImages([]);
    setImagePreviews([]);
    setImagesToDelete([]);
    setSelectedVideos([]);
    setVideoPreviews([]);
    setVideosToDelete([]);
    setActiveCategory('publish');
  };

  // Toggle HotSpot status
  const handleToggleHotSpot = async (id, currentFeatured) => {
    if (!window.confirm(currentFeatured ? 'Elimină acest anunț din HotSpot?' : 'Adaugă acest anunț în HotSpot?')) {
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/announcements/${id}/toggle-featured`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage(currentFeatured ? '✅ Anunț eliminat din HotSpot!' : '✅ Anunț adăugat în HotSpot!');
        fetchAllAnnouncements(); // Refresh list
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(data.message || 'Eroare la actualizarea HotSpot');
      }
    } catch (error) {
      console.error('Error toggling HotSpot:', error);
      setError('Eroare la actualizarea HotSpot');
    }
  };

  // Delete an announcement
  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest anunț? Această acțiune nu poate fi anulată.')) {
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/announcements/${id}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ Anunț șters cu succes!');
        fetchAllAnnouncements();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to delete announcement');
      }
    } catch (error) {
      setError('Server connection error');
      console.error(error);
    }
  };

  // Remove existing image (mark for deletion)
  const removeExistingImage = (imageName) => {
    setExistingImages(existingImages.filter(img => img !== imageName));
    setImagesToDelete([...imagesToDelete, imageName]);
  };

  // Reset form
  const resetForm = () => {
    setAnuntData({
      title: '',
      category: '',
      subcategory: '',
      subsubcategory: '',
      manufacturer: '',
      model: '',
      year: '',
      price: '',
      negotiable: true,
      featured: 0,
      condition: 'second-hand',
      hours: '',
      hoursUnit: 'hours',
      weight: '',
      power: '',
      capacity: '',
      features: '',
      description: '',
      location: '',
      contact: ''
    });
    setSelectedImages([]);
    setImagePreviews([]);
    setExistingImages([]);
    setImagesToDelete([]);
    setSelectedVideos([]);
    setVideoPreviews([]);
    setExistingVideos([]);
    setVideosToDelete([]);
    setEditingAnnouncement(null);
    setError('');
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    setUser(null);
    navigate('/admin-login');
  };

  if (!user) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <header className="admin-header">
        <div className="header-content">
          <h1>Admin Panel</h1>
          <div className="header-right">
            <span className="welcome-text">Welcome, <strong>{user.username}</strong></span>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      {/* Category Navigation */}
      <div className="category-nav">
        <button 
          className={`category-btn ${activeCategory === 'publish' ? 'active' : ''}`}
          onClick={() => {
            setActiveCategory('publish');
            resetForm();
          }}
        >
          📝 Publică Anunț
        </button>
        <button 
          className={`category-btn ${activeCategory === 'announcements' ? 'active' : ''}`}
          onClick={() => setActiveCategory('announcements')}
        >
          📋 Anunțuri ({announcements.length})
        </button>
      </div>

      {/* Main Content */}
      <div className="admin-content">
        <div className="content-wrapper">
          
          {/* PUBLISH/EDIT ANNOUNCEMENT SECTION */}
          {activeCategory === 'publish' && (
            <>
              <div className="page-header">
                <h2>{editingAnnouncement ? '✏️ Editează Anunț' : '📝 Publica Anunț Nou'}</h2>
                <p>{editingAnnouncement ? 'Modifică informațiile anunțului' : 'Adaugă o nouă mașină/utilaj în baza de date'}</p>
                {editingAnnouncement && (
                  <button onClick={resetForm} className="cancel-edit-btn">
                    ← Înapoi la Publicare Nou
                  </button>
                )}
              </div>

              {message && <div className="success-message">{message}</div>}
              {error && <div className="error-message">{error}</div>}

              <form onSubmit={handlePublishAnunt} className="anunt-form">
            <div className="form-grid">
              {/* Title */}
              <div className="form-group full-width">
                <label htmlFor="title">Titlu Anunț *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={anuntData.title}
                  onChange={handleInputChange}
                  placeholder="Ex: Excavator CAT 320D - Stare Excelenta"
                  required
                />
              </div>

              {/* Category */}
              <div className="form-group">
                <label htmlFor="category">Categorie Principală *</label>
                <select
                  id="category"
                  name="category"
                  value={anuntData.category}
                  onChange={handleCategoryChange}
                  required
                >
                  <option value="">Selectează categoria principală</option>
                  {categoryStructure.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory - shows only if category is selected */}
              {anuntData.category && getSubcategories().length > 0 && (
                <div className="form-group">
                  <label htmlFor="subcategory">Subcategorie *</label>
                  <select
                    id="subcategory"
                    name="subcategory"
                    value={anuntData.subcategory}
                    onChange={handleSubcategoryChange}
                    required
                  >
                    <option value="">Selectează subcategoria</option>
                    {getSubcategories().map(subcat => (
                      <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Sub-subcategory - shows only if subcategory has items */}
              {anuntData.subcategory && getSubSubcategories().length > 0 && (
                <div className="form-group">
                  <label htmlFor="subsubcategory">Tip specific</label>
                  <select
                    id="subsubcategory"
                    name="subsubcategory"
                    value={anuntData.subsubcategory}
                    onChange={handleInputChange}
                  >
                    <option value="">Selectează tipul (opțional)</option>
                    {getSubSubcategories().map((item, index) => {
                      const itemId = item.toLowerCase().replace(/\s+/g, '-');
                      return (
                        <option key={index} value={itemId}>{item}</option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Manufacturer */}
              <div className="form-group">
                <label htmlFor="manufacturer">Producător *</label>
                <input
                  type="text"
                  id="manufacturer"
                  name="manufacturer"
                  value={anuntData.manufacturer}
                  onChange={handleInputChange}
                  placeholder="Ex: New Holland, Caterpillar"
                  required
                />
              </div>

              {/* Model */}
              <div className="form-group">
                <label htmlFor="model">Model *</label>
                <input
                  type="text"
                  id="model"
                  name="model"
                  value={anuntData.model}
                  onChange={handleInputChange}
                  placeholder="Ex: W230 C"
                  required
                />
              </div>

              {/* Year */}
              <div className="form-group">
                <label htmlFor="year">An Fabricație *</label>
                <input
                  type="number"
                  id="year"
                  name="year"
                  value={anuntData.year}
                  onChange={handleInputChange}
                  placeholder="Ex: 2014"
                  required
                  min="1950"
                  max={new Date().getFullYear() + 1}
                />
              </div>

              {/* Hours/Kilometers */}
              <div className="form-group">
                <label htmlFor="hours">
                  {anuntData.hoursUnit === 'hours' ? 'Ore Funcționare' : 'Kilometri'}
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select
                    name="hoursUnit"
                    value={anuntData.hoursUnit}
                    onChange={handleInputChange}
                    style={{ width: '120px' }}
                  >
                    <option value="hours">Ore (H)</option>
                    <option value="km">Kilometri (Km)</option>
                  </select>
                  <input
                    type="number"
                    id="hours"
                    name="hours"
                    value={anuntData.hours}
                    onChange={handleInputChange}
                    placeholder={anuntData.hoursUnit === 'hours' ? 'Ex: 13000' : 'Ex: 150000'}
                    min="0"
                    style={{ flex: 1 }}
                  />
                </div>
                <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                  Optional - Lasă gol dacă nu este cazul
                </small>
              </div>

              {/* Price */}
              <div className="form-group">
                <label htmlFor="price">Preț (EUR) *</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={anuntData.price}
                  onChange={handleInputChange}
                  placeholder="Ex: 31900"
                  required
                  min="0"
                />
              </div>

              {/* Negotiable */}
              <div className="form-group">
                <label htmlFor="negotiable">Negociabil</label>
                <select
                  id="negotiable"
                  name="negotiable"
                  value={anuntData.negotiable}
                  onChange={handleInputChange}
                >
                  <option value="true">Da</option>
                  <option value="false">Nu</option>
                </select>
              </div>

              {/* Featured / HotSpot */}
              <div className="form-group">
                <label htmlFor="featured" className="hotspot-label">
                  🔥 HotSpot (Anunț Promovat)
                </label>
                <select
                  id="featured"
                  name="featured"
                  value={anuntData.featured}
                  onChange={handleInputChange}
                >
                  <option value="0">Nu</option>
                  <option value="1">Da - Apare în HotSpot</option>
                </select>
                <small className="form-hint">Anunțurile HotSpot apar evidențiate și în meniul special</small>
              </div>

              {/* Condition */}
              <div className="form-group">
                <label htmlFor="condition">Stare Utilaj *</label>
                <select
                  id="condition"
                  name="condition"
                  value={anuntData.condition}
                  onChange={handleInputChange}
                  required
                >
                  <option value="second-hand">Second Hand</option>
                  <option value="nou">Nou</option>
                  <option value="reconditionat">Reconditionat</option>
                </select>
              </div>

              {/* Weight */}
              <div className="form-group">
                <label htmlFor="weight">Greutate (tone)</label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={anuntData.weight}
                  onChange={handleInputChange}
                  placeholder="Ex: 22"
                  min="0"
                  step="0.1"
                />
              </div>

              {/* Power */}
              <div className="form-group">
                <label htmlFor="power">Putere Motor (CP)</label>
                <input
                  type="number"
                  id="power"
                  name="power"
                  value={anuntData.power}
                  onChange={handleInputChange}
                  placeholder="Ex: 272"
                  min="0"
                />
              </div>

              {/* Capacity */}
              <div className="form-group">
                <label htmlFor="capacity">Capacitate Cupă (mc)</label>
                <input
                  type="number"
                  id="capacity"
                  name="capacity"
                  value={anuntData.capacity}
                  onChange={handleInputChange}
                  placeholder="Ex: 5"
                  min="0"
                  step="0.1"
                />
              </div>

              {/* Location */}
              <div className="form-group">
                <label htmlFor="location">Locație *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={anuntData.location}
                  onChange={handleInputChange}
                  placeholder="Ex: Cluj-Napoca, Romania"
                  required
                />
              </div>

              {/* Contact */}
              <div className="form-group">
                <label htmlFor="contact">Contact (Telefon)</label>
                <input
                  type="text"
                  id="contact"
                  name="contact"
                  value={anuntData.contact}
                  onChange={handleInputChange}
                  placeholder="Ex: +40 123 456 789"
                />
              </div>

              {/* Images Upload */}
              <div className="form-group full-width">
                <label htmlFor="images">
                  Imagini ({(existingImages.length + selectedImages.length)}/10 imagini)
                  {selectedImages.length > 0 && <span className="success-text"> ✓ {selectedImages.length} noi</span>}
                </label>
                
                {/* Existing Images (for edit mode) */}
                {existingImages.length > 0 && (
                  <div className="existing-images-section">
                    <h4>Imagini existente ({existingImages.length})</h4>
                    <div className="image-previews">
                      {existingImages.map((imageName, index) => (
                        <div key={`existing-${index}`} className="image-preview-item existing">
                          <div className="preview-number">{index + 1}</div>
                          <img 
                            src={getUploadUrl(imageName)} 
                            alt={`Existing ${index + 1}`} 
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(imageName)}
                            className="remove-image-btn"
                            title="Șterge imagine"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* New Images Upload */}
                <input
                  type="file"
                  id="images"
                  name="images"
                  onChange={handleImageChange}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  multiple
                  className="file-input"
                  disabled={(existingImages.length + selectedImages.length) >= 10}
                />
                <p className="file-hint">
                  📁 Formate acceptate: JPG, PNG, GIF, WEBP (max 5MB fiecare)
                  <br />
                  💡 Poți selecta multiple imagini deodată sau adăuga una câte una
                  {selectedImages.length > 0 && ` • ${selectedImages.length} imagini noi selectate`}
                  {(existingImages.length + selectedImages.length) >= 10 && ' • Limită atinsă (10 imagini max)'}
                </p>
                
                {/* New Image Previews */}
                {imagePreviews.length > 0 && (
                  <div className="image-previews-section">
                    <div className="previews-header">
                      <h4>Imagini noi ({imagePreviews.length})</h4>
                      {(existingImages.length + selectedImages.length) < 10 && (
                        <label htmlFor="images" className="add-more-btn">
                          + Adaugă mai multe imagini
                        </label>
                      )}
                    </div>
                    <div className="image-previews">
                      {imagePreviews.map((preview, index) => (
                        <div key={`new-${index}`} className="image-preview-item">
                          <div className="preview-number">{existingImages.length + index + 1}</div>
                          <img src={preview} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="remove-image-btn"
                            title="Șterge imagine"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Video Upload Section */}
              <div className="form-group full-width">
                <label htmlFor="videos">
                  🎬 Video ({(existingVideos.length + selectedVideos.length)}/3 videoclipuri)
                  {selectedVideos.length > 0 && <span className="success-text"> ✓ {selectedVideos.length} noi</span>}
                </label>
                <input
                  type="file"
                  id="videos"
                  name="videos"
                  accept="video/mp4,video/quicktime,video/x-msvideo,video/x-ms-wmv,video/x-flv,video/webm,video/x-matroska"
                  onChange={handleVideoChange}
                  multiple
                  disabled={(existingVideos.length + selectedVideos.length) >= 3}
                />
                <p className="file-hint">
                  Formate acceptate: MP4, MOV, AVI, WMV, FLV, WebM, MKV • Max 100MB per video
                  {selectedVideos.length > 0 && ` • ${selectedVideos.length} videoclipuri noi selectate`}
                  {(existingVideos.length + selectedVideos.length) >= 3 && ' • Limită atinsă (3 videoclipuri max)'}
                </p>
                
                {/* Existing Videos */}
                {existingVideos.length > 0 && (
                  <div className="existing-videos-section">
                    <h4>Videoclipuri existente ({existingVideos.length})</h4>
                    <div className="video-list">
                      {existingVideos.map((video, index) => (
                        <div key={`existing-vid-${index}`} className="video-item">
                          <div className="video-icon">🎬</div>
                          <div className="video-info">
                            <span className="video-name">{video}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setVideosToDelete([...videosToDelete, video]);
                              setExistingVideos(existingVideos.filter((_, i) => i !== index));
                            }}
                            className="remove-video-btn"
                            title="Șterge videoclip"
                          >
                            🗑️ Șterge
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* New Video Previews */}
                {videoPreviews.length > 0 && (
                  <div className="video-previews-section">
                    <div className="previews-header">
                      <h4>Videoclipuri noi ({videoPreviews.length})</h4>
                      {(existingVideos.length + selectedVideos.length) < 3 && (
                        <label htmlFor="videos" className="add-more-btn">
                          + Adaugă mai multe videoclipuri
                        </label>
                      )}
                    </div>
                    <div className="video-list">
                      {videoPreviews.map((preview, index) => (
                        <div key={`new-vid-${index}`} className="video-item">
                          <div className="video-icon">🎬</div>
                          <div className="video-info">
                            <span className="video-name">{preview.name}</span>
                            <span className="video-size">{preview.size}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="remove-video-btn"
                            title="Șterge videoclip"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="form-group full-width">
                <label htmlFor="features">Caracteristici (câte una per linie)</label>
                <textarea
                  id="features"
                  name="features"
                  value={anuntData.features}
                  onChange={handleInputChange}
                  placeholder="Ex:&#10;motor 272 CP&#10;cupă 5 mc&#10;22 tone&#10;cauciucuri 80% bune&#10;Stare foarte bună de funcționare"
                  rows="5"
                />
              </div>

              {/* Description */}
              <div className="form-group full-width">
                <label htmlFor="description">Descriere *</label>
                <textarea
                  id="description"
                  name="description"
                  value={anuntData.description}
                  onChange={handleInputChange}
                  placeholder="Descriere detaliată a utilajului, stare tehnică, ore de funcționare, etc."
                  rows="6"
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingAnnouncement ? '� Actualizează Anunțul' : '�📢 Publică Anunțul'}
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={resetForm}
              >
                {editingAnnouncement ? 'Anulează Editarea' : 'Resetează'}
              </button>
            </div>
          </form>
            </>
          )}

          {/* ANNOUNCEMENTS LIST SECTION */}
          {activeCategory === 'announcements' && (
            <>
              <div className="page-header">
                <h2>📋 Toate Anunțurile</h2>
                <p>Gestionează, editează sau șterge anunțurile existente</p>
              </div>

              {message && <div className="success-message">{message}</div>}
              {error && <div className="error-message">{error}</div>}

              {/* Search Bar */}
              <div className="admin-search-bar">
                <input
                  type="text"
                  placeholder="🔍 Caută după titlu, categorie, fabricant, model, an..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="admin-search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="admin-clear-search"
                  >
                    ✕
                  </button>
                )}
                <div className="admin-search-results-count">
                  {announcements.filter(announcement => {
                    const query = searchQuery.toLowerCase();
                    return (
                      announcement.title?.toLowerCase().includes(query) ||
                      announcement.category?.toLowerCase().includes(query) ||
                      announcement.subcategory?.toLowerCase().includes(query) ||
                      announcement.manufacturer?.toLowerCase().includes(query) ||
                      announcement.model?.toLowerCase().includes(query) ||
                      announcement.year?.toString().includes(query) ||
                      announcement.location?.toLowerCase().includes(query)
                    );
                  }).length} rezultate găsite
                </div>
              </div>

              <div className="announcements-list">
                {announcements.length === 0 ? (
                  <div className="no-announcements">
                    <p>📭 Nu există anunțuri publicate încă.</p>
                    <button 
                      onClick={() => setActiveCategory('publish')}
                      className="create-first-btn"
                    >
                      Publică primul anunț
                    </button>
                  </div>
                ) : (
                  <div className="announcements-grid">
                    {announcements
                      .filter(announcement => {
                        if (!searchQuery) return true;
                        const query = searchQuery.toLowerCase();
                        return (
                          announcement.title?.toLowerCase().includes(query) ||
                          announcement.category?.toLowerCase().includes(query) ||
                          announcement.subcategory?.toLowerCase().includes(query) ||
                          announcement.manufacturer?.toLowerCase().includes(query) ||
                          announcement.model?.toLowerCase().includes(query) ||
                          announcement.year?.toString().includes(query) ||
                          announcement.location?.toLowerCase().includes(query)
                        );
                      })
                      .map((announcement) => (
                      <div key={announcement.id} className={`announcement-card ${announcement.featured ? 'hotspot-featured' : ''}`}>
                        <div className="announcement-image">
                          {announcement.images ? (
                            <img 
                              src={getUploadUrl(announcement.images.split(',')[0].trim())}
                              alt={announcement.title}
                            />
                          ) : (
                            <div className="no-image">📷 Fără imagine</div>
                          )}
                          <div className="announcement-status">
                            {announcement.status === 'active' ? '🟢 Activ' : '🔴 Inactiv'}
                          </div>
                          {announcement.featured === 1 && (
                            <div className="hotspot-badge">
                              🔥 HotSpot
                            </div>
                          )}
                        </div>
                        
                        <div className="announcement-details">
                          <h3>{announcement.title}</h3>
                          <div className="announcement-info">
                            <span className="info-item">
                              🏷️ {announcement.category}
                            </span>
                            <span className="info-item">
                              🏭 {announcement.manufacturer} {announcement.model}
                            </span>
                            <span className="info-item">
                              📅 {announcement.year}
                            </span>
                            <span className="info-item">
                              ⏱️ {announcement.hours} ore
                            </span>
                            <span className="info-item price">
                              💶 €{Number(announcement.price).toLocaleString()}
                              {(announcement.negotiable === 1 || announcement.negotiable === true) && (
                                <span className="negotiable-tag"> Negociabil</span>
                              )}
                            </span>
                            <span className="info-item">
                              📍 {announcement.location}
                            </span>
                            <span className="info-item">
                              👁️ {announcement.views || 0} vizualizări
                            </span>
                            <span className="info-item">
                              📆 {new Date(announcement.created_at).toLocaleDateString('ro-RO')}
                            </span>
                          </div>
                          
                          {announcement.description && (
                            <p className="announcement-description">
                              {announcement.description.substring(0, 150)}
                              {announcement.description.length > 150 ? '...' : ''}
                            </p>
                          )}
                        </div>
                        
                        <div className="announcement-actions">
                          <button 
                            onClick={() => handleToggleHotSpot(announcement.id, announcement.featured)}
                            className={`hotspot-toggle-btn ${announcement.featured ? 'active' : ''}`}
                            title={announcement.featured ? 'Elimină din HotSpot' : 'Adaugă în HotSpot'}
                          >
                            🔥 {announcement.featured ? 'În HotSpot' : 'Marchează HotSpot'}
                          </button>
                          <button 
                            onClick={() => handleEditAnnouncement(announcement)}
                            className="edit-btn"
                            title="Editează anunțul"
                          >
                            ✏️ Editează
                          </button>
                          <button 
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                            className="delete-btn"
                            title="Șterge anunțul"
                          >
                            🗑️ Șterge
                          </button>
                          <a 
                            href={getAnnouncementUrl(announcement.id, announcement.title, announcement.category, announcement.subcategory)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="view-btn"
                            title="Vizualizează anunțul"
                          >
                            👁️ Vizualizează
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminPanel;